import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  FlatList,
  ScrollView,
  useWindowDimensions,
  Text,
  View,
} from 'react-native';
import { Button, Snackbar } from 'react-native-paper';
import { useForm, FormProvider } from 'react-hook-form';
import { merge } from 'lodash';

import Question from './Question';
import StepFormNavigation from '../components/StepFormNavigation';
import createStyles, { FORM_MAX_WIDTH } from './style';
import { updateQuestionnaireFlow } from './hooks/useFlowHook';
import { shallowEqual } from 'react-redux';
import { IQuestionItem, IQuestionnaire } from '@nocode/types';
import { isCanvas } from '@common/utils';
import PreviewCms from './PreviewCms';
import useQuestionnaire, {
  useProcessQuestions,
  DISPLAY_QUESTIONS,
} from './hooks/hooks';
import QuestionHeader from './QuestionHeader';
import { getFormControlType } from './helpers';
import {
  COVER_IMAGE_CROPPING,
  COVER_IMAGE_TYPE,
} from '@nocode/types/questionnaire.type';

const Questionnaire: React.FC<IQuestionnaire> = (props) => {
  const {
    configs,
    defaultQuestions,
    style: styleConfig,
    isPreview,
    questions,
    id: formId,
  } = props;
  const methods = useForm();
  const flatlist = useRef<FlatList<any>>(null);
  const canvas = isCanvas();

  let dimension = useWindowDimensions();
  let height = dimension.height;
  let width =
    dimension.width > FORM_MAX_WIDTH ? FORM_MAX_WIDTH : dimension.width;

  const [data, setData] = useState<IQuestionItem[]>([]);
  const [processedQuestions, setProcessedQuestions] = useState<
    Record<string, unknown>[]
  >([]);

  const {
    currentIndex,
    isSubmitting,
    snackbar,
    hideSnackbar,
    onNextButtonClick,
    onPrevButtonClick,
    onSubmit,
    formInstanceId,
  } = useQuestionnaire({ configs, methods, props, filteredData: data });

  const formValuesRef = useRef({});

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialScrollRef = useRef(true);

  useEffect(() => {
    if (canvas && defaultQuestions) {
      let displayQuestion = defaultQuestions?.[0];

      if (configs?.activeChildComponent) {
        const questionPool = [...defaultQuestions, ...DISPLAY_QUESTIONS];
        const control = getFormControlType(configs.activeChildComponent);

        const selected = questionPool.find(
          (question) => question.control === control
        );

        if (selected) {
          displayQuestion = selected;
        }
      }

      setData([
        {
          ...displayQuestion,
          ...(configs?.activeChildComponent === 'coverImage'
            ? {
                coverImage: {
                  enabled: true,
                  type: COVER_IMAGE_TYPE.IMAGE,
                  url: 'https://mikosea.s3.ap-northeast-1.amazonaws.com/common/green_coffe_mug.jpg',
                  cropping: COVER_IMAGE_CROPPING.COVER,
                },
              }
            : {}),
          note: {
            enabled: true,
            text: 'この質問はプレビューのみです。このテキストはCMSで自由に非表示にしたり編集したりできます。',
          },
        },
        ...defaultQuestions,
      ]);
    }
  }, [configs?.activeChildComponent]);

  useEffect(() => {
    if (!flatlist?.current) return;

    flatlist.current.scrollToOffset({
      animated: true,
      offset: width * currentIndex,
    });
    if (isInitialScrollRef.current) {
      isInitialScrollRef.current = false;
    }
  }, [currentIndex, width, formInstanceId, formId]);

  // Reset scroll position and initial scroll flag when form ID changes
  useEffect(() => {
    isInitialScrollRef.current = true;
    if (flatlist?.current) {
      flatlist.current.scrollToOffset({
        animated: false,
        offset: 0,
      });
    }
  }, [formId, formInstanceId]);

  useEffect(() => {
    if (!canvas && questions) {
      // Process the questions and set up default values
      const processed = useProcessQuestions(questions);
      setProcessedQuestions(processed);
      // Get current form values to preserve user input
      const currentFormValues = methods.getValues();
      const defaultValues = questions.reduce((acc: any, question: any) => {
        const fieldName = question.fid || `question-${question.id}`;
        // Preserve existing value if it exists (including empty strings after user edits), otherwise use default
        const hasExistingValue = fieldName in currentFormValues;
        acc[fieldName] = hasExistingValue
          ? currentFormValues[fieldName]
          : question.defaultValue || '';
        return acc;
      }, {});
      methods.reset(defaultValues);
      formValuesRef.current = defaultValues;
      const filteredQuestions = updateQuestionnaireFlow(
        processed,
        defaultValues
      );
      setData(filteredQuestions);
    }
  }, [questions, canvas, formId]);

  const updateQuestionnaireData = useCallback(
    (formValues: any) => {
      if (!processedQuestions.length || canvas) return;

      if (shallowEqual(formValuesRef.current, formValues)) return;
      formValuesRef.current = { ...formValues };
      const filteredQuestions = updateQuestionnaireFlow(
        processedQuestions,
        formValues
      );
      setData(filteredQuestions);

      if (
        currentIndex >= filteredQuestions.length &&
        filteredQuestions.length > 0
      ) {
        const newIndex = Math.max(0, filteredQuestions.length - 1);

        if (flatlist?.current) {
          flatlist.current.scrollToOffset({
            animated: true,
            offset: width * newIndex,
          });
        }
      }
    },
    [processedQuestions, canvas, currentIndex, width, data.length]
  );

  useEffect(() => {
    if (!canvas) {
      const subscription = methods.watch((formValues) => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
          updateQuestionnaireData(formValues);
        }, 100);
      });

      return () => {
        subscription.unsubscribe();
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }
  }, [updateQuestionnaireData, canvas]);

  if (isPreview) {
    return <PreviewCms {...props} />;
  }

  let manifest;
  if (props?.breakpointManifest) {
    const { breakpointManifest } = props;
    if (dimension.width < 768) {
      manifest = breakpointManifest.find(
        (item: Record<string, any>) => item['key'] === 'MOBILE'
      );
    } else if (dimension.width < 1200 && dimension.width >= 768) {
      manifest = breakpointManifest.find(
        (item: Record<string, any>) => item['key'] === 'TABLET'
      );
      width = 600;
    } else if (dimension.width >= 1200) {
      manifest = breakpointManifest.find(
        (item: Record<string, any>) => item['key'] === 'DESKTOP'
      );
      width = 800;
    }
  }

  if (canvas) {
    height = props.height;
    width = props.width;
  }

  const mergedStyles = merge(styleConfig, manifest?.style);
  const styleSheet = createStyles(mergedStyles);

  const stepFormButtons =
    configs?.formType === 'step-form' && data && data.length > 0 && !canvas ? (
      <View
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          width: '100%',
          flexDirection: 'row',
          paddingHorizontal: 20,
          justifyContent: 'center',
          paddingVertical: 20,
          backgroundColor: styleConfig?.backgroundColor || '#F4F4F4',
          borderTopWidth: 1,
          borderTopColor: '#E0E0E0',
          zIndex: 9999,
        }}
      >
        <StepFormNavigation
          onNextButtonClick={onNextButtonClick}
          onPrevButtonClick={onPrevButtonClick}
          styles={styleSheet}
          disabledBack={isSubmitting || currentIndex === 0}
          disabledForward={isSubmitting}
          isSubmitting={isSubmitting}
        />
      </View>
    ) : null;

  const snackbarPortal = !canvas ? (
    <Snackbar
      visible={snackbar.visible}
      onDismiss={hideSnackbar}
      duration={snackbar.duration}
      style={[
        snackbar.style,
        {
          position: 'fixed',
          bottom: 90,
          left: 10,
          right: 10,
          marginLeft: 'auto',
          marginRight: 'auto',
          zIndex: 99999,
          maxWidth: 500,
        },
      ]}
      action={
        snackbar.showAction
          ? {
              label: '閉じる',
              onPress: hideSnackbar,
            }
          : undefined
      }
    >
      {snackbar.message}
    </Snackbar>
  ) : null;

  return (
    <FormProvider {...methods}>
      <View
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...(canvas ? { width, height } : {}),
        }}
      >
        <View
          style={[
            styleSheet.container,
            {
              width,
            },
          ]}
        >
          {data && data.length > 0 && configs?.formType === 'step-form' ? (
            <QuestionHeader
              canvas={canvas}
              currentIndex={currentIndex}
              questions={data as unknown as IQuestionItem[]}
              styleConfig={mergedStyles}
              configs={configs}
            />
          ) : null}

          {canvas ? (
            <Question
              item={data[0]}
              width={width}
              formType={configs?.formType}
              styleConfig={mergedStyles}
              index={0}
            />
          ) : (
            <FlatList
              data={canvas ? data.slice(0, 1) : data}
              ref={flatlist}
              renderItem={({ item, index }) =>
                configs?.formType === 'step-form' ? (
                  <ScrollView
                    style={{
                      width,
                      height: '100%',
                    }}
                    contentContainerStyle={{
                      paddingBottom: 90,
                    }}
                  >
                    <Question
                      item={item}
                      index={index}
                      questionMarker={configs?.questionMarker}
                      formType={configs?.formType}
                      styleConfig={mergedStyles}
                      width={width}
                    />
                  </ScrollView>
                ) : (
                  <Question
                    item={item}
                    index={index}
                    questionMarker={configs?.questionMarker}
                    formType={configs?.formType}
                    styleConfig={mergedStyles}
                    width={width}
                  />
                )
              }
              snapToAlignment="start"
              keyExtractor={(item, index) =>
                item?.id?.toString() || index.toString()
              }
              {...(configs?.formType === 'step-form'
                ? {
                    getItemLayout: (_data, index) => ({
                      length: width,
                      offset: width * index,
                      index,
                    }),
                    horizontal: true,
                    pagingEnabled: true,
                    initialScrollIndex: 0,
                    showsHorizontalScrollIndicator: false,
                    scrollEnabled: false,
                  }
                : {
                    initialScrollIndex: 0,
                    scrollEnabled: true,
                    getItemLayout: (_data, index) => ({
                      length: width,
                      offset: height * index,
                      index,
                    }),
                  })}
              style={{
                width,
                // maxWidth: FORM_MAX_WIDTH,
                flex: 1,
              }}
            />
          )}

          {configs?.formType !== 'step-form' && (
            <View
              style={{
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                paddingHorizontal: 20,
                justifyContent: 'center',
                paddingVertical: 20,
              }}
            >
              {data && data.length > 0 && (
                <Button
                  style={[
                    styleSheet.commonButtonWrapper,
                    styleSheet.submitButton,
                    styleSheet.nextButtonWrapper,
                  ]}
                  contentStyle={styleSheet.commonButtonContent}
                  labelStyle={[
                    styleSheet.commonButtonLabel,
                    styleSheet.nextButtonLabel,
                  ]}
                  onPress={methods.handleSubmit(onSubmit)}
                  disabled={isSubmitting || canvas}
                  loading={isSubmitting}
                >
                  {configs?.submitButtonText || '送信'}
                </Button>
              )}
            </View>
          )}
        </View>
        {/* Render step-form buttons inline for canvas mode */}
        {configs?.formType === 'step-form' &&
          canvas &&
          data &&
          data.length > 0 && (
            <View
              style={{
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                paddingHorizontal: 20,
                justifyContent: 'center',
                paddingVertical: 20,
                backgroundColor: mergedStyles?.backgroundColor || 'white',
                borderTopWidth: 1,
                borderTopColor: '#E0E0E0',
              }}
            >
              <StepFormNavigation
                onNextButtonClick={onNextButtonClick}
                onPrevButtonClick={onPrevButtonClick}
                styles={styleSheet}
                disabledBack={isSubmitting || canvas || currentIndex === 0}
                disabledForward={isSubmitting || canvas}
                isSubmitting={isSubmitting}
              />
            </View>
          )}
      </View>

      {/* Render step-form buttons at root level via portal for non-canvas mode */}
      {typeof document !== 'undefined' &&
        stepFormButtons &&
        ReactDOM.createPortal(stepFormButtons, document.body)}

      {/* Render snackbar at root level via portal for non-canvas mode */}
      {typeof document !== 'undefined' &&
        snackbarPortal &&
        ReactDOM.createPortal(snackbarPortal, document.body)}
    </FormProvider>
  );
};

export default Questionnaire;
